import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTenantContext } from '../context/TenantContext';
import { alertsAPI } from '../services/api';
import { getAuthState } from '../services/auth';
import socketService from '../services/socket';
import { toast } from '../services/toast';
import './Navbar.css';

const normalizeList = (payload, key) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.[key])) return payload[key];
  return [];
};

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = getAuthState();
  const { tenants, selectedTenantId } = useTenantContext();
  const roleName = (user.role || 'user').toUpperCase();
  const [highRiskCount, setHighRiskCount] = useState(0);
  const [isSideNavOpen, setIsSideNavOpen] = useState(isAuthenticated);
  const [isMspSectionOpen, setIsMspSectionOpen] = useState(() => {
    const stored = localStorage.getItem('mspSectionOpen');
    if (stored === null) return true;
    return stored === 'true';
  });
  const [isScansSectionOpen, setIsScansSectionOpen] = useState(true);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    navigate('/home');
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setHighRiskCount(0);
      return;
    }

    const loadAlerts = async () => {
      try {
        const response = await alertsAPI.list({ status: 'open' });
        const responseAlerts = normalizeList(response.data, 'alerts');
        const highRiskAlerts = responseAlerts.filter((alert) => {
          const severity = (alert.severity || alert.risk || alert.priority || '').toLowerCase();
          return severity === 'high' || severity === 'critical';
        });
        setHighRiskCount(highRiskAlerts.length);
      } catch (error) {
        console.error('Error loading alert badge count:', error);
      }
    };

    loadAlerts();
  }, [isAuthenticated]);

  useEffect(() => {
    setIsSideNavOpen(isAuthenticated);
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      socketService.connect();
      
      const handleScanCompleted = (data) => {
        toast.success(`Scan Completed: ${data.scan_id}. Found ${data.total_findings} items.`);
      };

      const handleScanFailed = (data) => {
        toast.error(`Scan Failed: ${data.scan_id}. Error: ${data.error}`);
      };

      socketService.on('scan_completed', handleScanCompleted);
      socketService.on('scan_failed', handleScanFailed);

      return () => {
        socketService.off('scan_completed', handleScanCompleted);
        socketService.off('scan_failed', handleScanFailed);
      };
    }
  }, [isAuthenticated]);

  useEffect(() => {
    localStorage.setItem('mspSectionOpen', String(isMspSectionOpen));
  }, [isMspSectionOpen]);

  // The public home page uses its own marketing header when unauthenticated.
  if (location.pathname === '/home' && !isAuthenticated) {
    return null;
  }

  const selectedTenantName =
    tenants.find((tenant) => (tenant.id || tenant.tenant_id) === selectedTenantId)?.name ||
    tenants.find((tenant) => (tenant.id || tenant.tenant_id) === selectedTenantId)?.tenant_name ||
    '';

  const toggleSideNav = () => {
    setIsSideNavOpen((prev) => !prev);
  };

  const closeSideNav = () => {
    setIsSideNavOpen(false);
  };

  const toggleMspSection = () => {
    setIsMspSectionOpen((prev) => !prev);
  };

  const toggleScansSection = () => {
    setIsScansSectionOpen((prev) => !prev);
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          {isAuthenticated && (
            <button
              className="sidenav-toggle"
              onClick={toggleSideNav}
              aria-label="Toggle side menu"
              aria-expanded={isSideNavOpen}
              aria-controls="sidenav-menu"
              type="button"
            >
              <span className="sidenav-toggle-bar"></span>
              <span className="sidenav-toggle-bar"></span>
              <span className="sidenav-toggle-bar"></span>
              <span className="sidenav-toggle-text">Menu</span>
            </button>
          )}
          <Link to="/" className="navbar-brand">
            PassiveGuard
          </Link>
          <div className="navbar-links">
            {isAuthenticated ? (
              <>
                <div className="dropdown">
                  <Link to="/features/workflows" className="navbar-link">Workflows</Link>
                  <div className="dropdown-content">
                    <Link to="/docs/platform-overview" className="dropdown-item">Platform Overview & Core Components</Link>
                    <Link to="/docs/technical-implementation" className="dropdown-item">Technical Implementation & Data Flow</Link>
                    <Link to="/docs/advanced-features" className="dropdown-item">Advanced Features & Security Capabilities</Link>
                    <Link to="/docs/future-proofing" className="dropdown-item">Future-Proofing & Innovation</Link>
                  </div>
                </div>
                <div className="dropdown">
                  <Link to="/features/response" className="navbar-link">Response</Link>
                  <div className="dropdown-content">
                    <Link to="/docs/incident-response" className="dropdown-item">Incident Response & Automation Framework</Link>
                    <Link to="/docs/security-operations" className="dropdown-item">Security Operations & Best Practices</Link>
                    <Link to="/docs/continuous-improvement" className="dropdown-item">Continuous Improvement</Link>
                    <Link to="/docs/nist-csf" className="dropdown-item">NIST CSF Alignment</Link>
                  </div>
                </div>
                <div className="dropdown">
                  <Link to="/features/integrations" className="navbar-link">Integrations</Link>
                  <div className="dropdown-content">
                    <Link to="/docs/implementation-deployment" className="dropdown-item">Implementation & Deployment</Link>
                    <Link to="/docs/integration-capabilities" className="dropdown-item">Integration Capabilities</Link>
                    <Link to="/docs/monitoring-maintenance" className="dropdown-item">Monitoring & Maintenance</Link>
                  </div>
                </div>
                <Link to="/pricing" className="navbar-link">Pricing</Link>
                <Link to="/network-analyzer" className="navbar-link">Network Analyzer</Link>
                <Link to="/blog" className="navbar-link">Blog</Link>
                {user.role === 'admin' && (
                  <>
                    <Link to="/admin/users" className="navbar-link" style={{ color: 'var(--gh-danger)' }}>Users</Link>
                    <Link to="/admin/traffic" className="navbar-link" style={{ color: 'var(--gh-link)' }}>Traffic</Link>
                  </>
                )}
                <span className="navbar-user" style={{ color: user.role === 'admin' ? 'var(--gh-danger)' : 'var(--gh-text-muted)' }}>
                  {user.username || 'User'} [{roleName}]
                </span>
                <button onClick={handleLogout} className="navbar-link navbar-logout">
                  Logout
                </button>
              </>
            ) : (
              <>
                <div className="dropdown">
                  <Link to="/features/workflows" className="navbar-link">Workflows</Link>
                  <div className="dropdown-content">
                    <Link to="/docs/platform-overview" className="dropdown-item">Platform Overview & Core Components</Link>
                    <Link to="/docs/technical-implementation" className="dropdown-item">Technical Implementation & Data Flow</Link>
                    <Link to="/docs/advanced-features" className="dropdown-item">Advanced Features & Security Capabilities</Link>
                    <Link to="/docs/future-proofing" className="dropdown-item">Future-Proofing & Innovation</Link>
                  </div>
                </div>
                <div className="dropdown">
                  <Link to="/features/response" className="navbar-link">Response</Link>
                  <div className="dropdown-content">
                    <Link to="/docs/incident-response" className="dropdown-item">Incident Response & Automation Framework</Link>
                    <Link to="/docs/security-operations" className="dropdown-item">Security Operations & Best Practices</Link>
                    <Link to="/docs/continuous-improvement" className="dropdown-item">Continuous Improvement</Link>
                    <Link to="/docs/nist-csf" className="dropdown-item">NIST CSF Alignment</Link>
                  </div>
                </div>
                <div className="dropdown">
                  <Link to="/features/integrations" className="navbar-link">Integrations</Link>
                  <div className="dropdown-content">
                    <Link to="/docs/implementation-deployment" className="dropdown-item">Implementation & Deployment</Link>
                    <Link to="/docs/integration-capabilities" className="dropdown-item">Integration Capabilities</Link>
                    <Link to="/docs/monitoring-maintenance" className="dropdown-item">Monitoring & Maintenance</Link>
                  </div>
                </div>
                <Link to="/pricing" className="navbar-link">Pricing</Link>
                <Link to="/login" className="navbar-link">Login</Link>
                <Link to="/register" className="navbar-link">Register</Link>
              </>
            )}
          </div>
        </div>
      </nav>
      {isAuthenticated && (
        <>
          <div
            className={`sidenav-overlay ${isSideNavOpen ? 'open' : ''}`}
            onClick={closeSideNav}
            aria-hidden={!isSideNavOpen}
          ></div>
          <aside
            id="sidenav-menu"
            className={`sidenav ${isSideNavOpen ? 'open' : ''}`}
            aria-hidden={!isSideNavOpen}
          >
            <div className="sidenav-header">
              <span className="sidenav-title">Navigation</span>
              <button className="sidenav-close" onClick={closeSideNav} aria-label="Close side menu" type="button">
                X
              </button>
            </div>
            <nav className="sidenav-links">
              <Link to="/alerts" className="sidenav-link sidenav-alerts-link" onClick={closeSideNav}>
                Alerts
                {highRiskCount > 0 && (
                  <span className="navbar-badge" aria-label={`${highRiskCount} high risk alerts`}>
                    {highRiskCount}
                  </span>
                )}
              </Link>
              <div className="sidenav-group">
                <button
                  className="sidenav-group-toggle"
                  type="button"
                  onClick={toggleScansSection}
                  aria-expanded={isScansSectionOpen}
                >
                  <span>Scans</span>
                  <span className={`sidenav-group-chevron ${isScansSectionOpen ? 'open' : ''}`}>▾</span>
                </button>
                {isScansSectionOpen && (
                  <div className="sidenav-sub-links">
                    <Link to="/scans?type=internal" className="sidenav-link sidenav-sub-link" onClick={closeSideNav}>
                      Internal Scans
                    </Link>
                    <Link to="/external-scans" className="sidenav-link sidenav-sub-link" onClick={closeSideNav}>
                      External Scans
                    </Link>
                    <Link to="/network-analyzer" className="sidenav-link sidenav-sub-link" onClick={closeSideNav}>
                      Network Analyzer
                    </Link>
                    <Link to="/agents" className="sidenav-link sidenav-sub-link" onClick={closeSideNav}>
                      Agents
                    </Link>
                  </div>
                )}
              </div>
              <div className="sidenav-group">
                <button
                  className="sidenav-group-toggle"
                  type="button"
                  onClick={toggleMspSection}
                  aria-expanded={isMspSectionOpen}
                >
                  <span>MSP Console</span>
                  <span className={`sidenav-group-chevron ${isMspSectionOpen ? 'open' : ''}`}>▾</span>
                </button>
                {isMspSectionOpen && (
                  <div className="sidenav-sub-links">
                    <Link to="/msp" className="sidenav-link sidenav-sub-link" onClick={closeSideNav}>
                      MSP Console
                    </Link>
                    <Link to="/msp/tenants" className="sidenav-link sidenav-sub-link" onClick={closeSideNav}>
                      Tenants
                    </Link>
                    {selectedTenantId && (
                      <>
                        <Link
                          to={`/msp/tenants/${selectedTenantId}`}
                          className="sidenav-link sidenav-sub-link"
                          onClick={closeSideNav}
                        >
                          {selectedTenantName || 'Tenant Overview'}
                        </Link>
                        <Link
                          to={`/msp/tenants/${selectedTenantId}/assets`}
                          className="sidenav-link sidenav-sub-link"
                          onClick={closeSideNav}
                        >
                          Assets
                        </Link>
                        <Link
                          to={`/msp/tenants/${selectedTenantId}/findings`}
                          className="sidenav-link sidenav-sub-link"
                          onClick={closeSideNav}
                        >
                          Findings
                        </Link>
                        <Link
                          to={`/msp/tenants/${selectedTenantId}/alerts`}
                          className="sidenav-link sidenav-sub-link"
                          onClick={closeSideNav}
                        >
                          Alerts
                        </Link>
                        <Link
                          to={`/msp/tenants/${selectedTenantId}/reports`}
                          className="sidenav-link sidenav-sub-link"
                          onClick={closeSideNav}
                        >
                          Reports
                        </Link>
                        <Link
                          to={`/msp/tenants/${selectedTenantId}/external-scans`}
                          className="sidenav-link sidenav-sub-link"
                          onClick={closeSideNav}
                        >
                          External Scans
                        </Link>
                      </>
                    )}
                    <Link to="/msp/branding" className="sidenav-link sidenav-sub-link" onClick={closeSideNav}>
                      Branding
                    </Link>
                  </div>
                )}
              </div>
              <Link to="/external-assets" className="sidenav-link" onClick={closeSideNav}>External Assets</Link>
              <Link to="/onboarding" className="sidenav-link" onClick={closeSideNav}>Onboarding</Link>
            </nav>
          </aside>
        </>
      )}
    </>
  );
}

export default Navbar;
